import {
	getAdPageType,
	getAdPlacement,
	getAdPosition,
	getAditudeBaseId,
} from "@/common/functions";
import { AD_LAYOUT_STUBS } from "@/common/consts";
import ResizeObserver from "resize-observer-polyfill";

export default {
	name: "Advertising",
	props: {
		stub: {
			type: String,
			required: true,
		},
		adId: {
			type: String,
			required: false,
		},
		pageType: {
			type: String,
			required: false,
		},
		placement: {
			type: String,
			required: false,
		},
		position: {
			type: String,
			required: false,
		},
		loading: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			observer: null,
			isSticky: false,
			aditudeId: null,
		};
	},
	computed: {
		isDebugEnabled() {
			return (
				this.isFeatureEnabled("DebugMode") &&
				this.isFeatureEnabled("DebugAds")
			);
		},
		stickyTop() {
			const GAP_BELOW_HEADER = 22;
			return (
				GAP_BELOW_HEADER +
				this.$store.state.domElements.topAdHeight +
				this.$store.state.domElements.visibleHeaderHeight
			);
		},
		outerElementAdClasses() {
			const classes = ["ad"];

			const stubClass = `ad--${this.stub
				?.toLowerCase()
				?.replace(/_/g, "-")}`;
			classes.push(stubClass);

			if (this.isSticky) {
				classes.push("ad--sticky");
			}

			return classes;
		},
		innerElementAdClasses() {
			const classes = ["ad__inner"];
			if (this.stub === AD_LAYOUT_STUBS.MOB_IN_CONTENT) {
				classes.push("ad__inner--in-content");
			}

			if (this.innerElementIsScrollable) {
				classes.push("ad__inner--sticky");
			}

			return classes;
		},
		innerElementIsScrollable() {
			switch (this.stub) {
				case AD_LAYOUT_STUBS.ADHESION:
				case AD_LAYOUT_STUBS.TOP_LEADERBOARD:
					return false;
				default:
					return true;
			}
		},
		_pageType() {
			if (typeof this.pageType === "string" && this.pageType.length)
				return this.pageType;

			return getAdPageType(this.$route.name);
		},
		_placement() {
			if (typeof this.placement === "string" && this.placement.length)
				return this.placement;

			return getAdPlacement(this.stub);
		},
		_position() {
			if (typeof this.position === "string" && this.position.length)
				return this.position;

			return getAdPosition(this._placement);
		},
		aditudeBaseId() {
			return getAditudeBaseId(this._placement);
		},
		isPersistentAd() {
			return [
				AD_LAYOUT_STUBS.ADHESION,
				AD_LAYOUT_STUBS.TOP_LEADERBOARD,
			].includes(this.stub);
		},
		targetingString() {
			const data = {};
			if (this._pageType) {
				data.pagetype = this._pageType;
			}
			if (this._placement) {
				data.placement = this._placement;
			}
			if (this._position) {
				data.position = this._position;
			}
			if (this.bucketValue) {
				data.bucket_value = this.bucketValue;
			}
			return JSON.stringify(data);
		},
	},
	methods: {
		setTopAdHeight() {
			this.$store.commit(
				"setTopAdHeight",
				this.$el.parentElement.clientHeight
			);
		},
		refreshAditudeAd() {
			window.tude = window.tude || { cmd: [] };
			window?.tude.cmd.push(() => {
				if (this.isDebugEnabled) {
					// eslint-disable-next-line no-console
					console.debug("ADITUDE CMD > REFRESH", this.aditudeId);
				}
				window?.tude.refreshAdsViaDivMappings([
					{
						divId: this.aditudeId,
						baseDivId: this.aditudeBaseId,
						targeting: JSON.parse(this.targetingString),
					},
				]);
			});
		},
	},
	created() {
		let aditudeId;
		if (typeof this.adId === "string" && this.adId.length) {
			aditudeId = this.adId;
		} else {
			const stubs = [this._placement];
			if (this.$route.name && !this.isPersistentAd) {
				stubs.push(this.$route.name);
			}
			aditudeId = stubs.join("_");
		}

		this.aditudeId = aditudeId;
	},
	mounted() {
		if (this.$el.classList.contains("advertising--sticky")) {
			this.isSticky = true;
		}

		if (
			this.stub === AD_LAYOUT_STUBS.TOP_LEADERBOARD &&
			!this.isSmallDevice
		) {
			this.observer = new ResizeObserver(this.setTopAdHeight).observe(
				this.$el
			);
		}

		if (!this.loading && window) {
			if (this.isDebugEnabled) {
				// eslint-disable-next-line no-console
				console.debug(
					`ADITUDE LOG > Loading Aditude ad with div ID ${this.aditudeId} and base div ID ${this.aditudeBaseId}.`
				);
			}
			this.refreshAditudeAd();
		}
	},
	beforeDestroy() {
		if (!this.loading) {
			if (this.isDebugEnabled) {
				// eslint-disable-next-line no-console
				console.debug(
					`ADITUDE LOG > About to destroy the Aditude ad with div ID ${this.aditudeId} with base div ID ${this.aditudeBaseId}.`
				);
			}
			window?.tude.cmd.push(() => {
				if (this.isDebugEnabled) {
					// eslint-disable-next-line no-console
					console.debug("ADITUDE CMD > DESTROY", this.aditudeId);
				}
				window.tude.destroyAds([this.aditudeId], false);
			});
		}
	},
	watch: {
		$route(to, from) {
			if (
				this.isPersistentAd &&
				from.name &&
				from.fullPath !== to.fullPath
			) {
				this.refreshAditudeAd();
			}
		},
	},
};
